import React from 'react';
import { Grid, TextField, InputAdornment, Tooltip } from '@mui/material';
import { floatVal } from '@jamesgmarks/utilities';
import { parsePercentageInput, roundFloatToCents } from '@hydra/internal';
import {
  assertValidBillingType,
  BILLING_FEATURE_BASE_PRICE,
  billingTypeHasFeature,
  TValidBillingFeature,
} from '@llws/hydra-shared';
import InfoIcon from '@mui/icons-material/Info';
import { ISubscriptionData } from '../types';
import { getFieldAnimationStyles, getRequiredFieldPulseStyles } from 'src/components/pages/Subscriptions/form/animationStyles';

interface ISubscriptionPricingProps {
  subscriptionData: ISubscriptionData;
  setSubscriptionData: React.Dispatch<React.SetStateAction<ISubscriptionData>>;
  discountRateFormValue: string;
  setDiscountRateFormValue: React.Dispatch<React.SetStateAction<string>>;
  billingTypeId: number;
  billingFeatureMetadata: (billingTypeId: number, feature: TValidBillingFeature, field: string) => unknown;
}

export const SubscriptionPricing: React.FC<ISubscriptionPricingProps> = ({
  subscriptionData,
  setSubscriptionData,
  discountRateFormValue,
  setDiscountRateFormValue,
  billingTypeId,
  billingFeatureMetadata,
}) => {
  return (
    <Grid container spacing={3}>
      {/* Base Price Fields */}
      {
        billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_BASE_PRICE)
        && <>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label={
                billingFeatureMetadata(billingTypeId, BILLING_FEATURE_BASE_PRICE, 'label')
                  ? (`${billingFeatureMetadata(billingTypeId, BILLING_FEATURE_BASE_PRICE, 'label')}`)
                  : 'Base Price'
              }
              type="number"
              size="small"
              fullWidth
              sx={{ background: "white" }}
              value={roundFloatToCents(subscriptionData.basePrice ?? 0)}
              onChange={(e) => {
                const value = floatVal(e.target.value);
                if (value < 0) return;
                setSubscriptionData((old) => (
                  { ...old, basePrice: roundFloatToCents(value) }
                ));
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

          {
            billingFeatureMetadata(billingTypeId, BILLING_FEATURE_BASE_PRICE, 'hasScalar')
            && <Grid item xs={12} sm={6} md={4}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label={
                    billingFeatureMetadata(billingTypeId, BILLING_FEATURE_BASE_PRICE, 'label')
                      ? `${billingFeatureMetadata(billingTypeId, BILLING_FEATURE_BASE_PRICE, 'scalarLabel')
                      }`
                      : 'Base Price Scalar'
                  }
                  type="number"
                  sx={{ background: "white", marginRight: '0.5rem' }}
                  inputProps={{
                    step: 0.1,
                  }}
                  size="small"
                  fullWidth
                  value={roundFloatToCents(subscriptionData.basePriceScalar ?? 0)}
                  onChange={(e) => {
                    const value = floatVal(e.target.value);
                    if (value < 0) return;
                    setSubscriptionData((old) => (
                      { ...old, basePriceScalar: roundFloatToCents(value) }
                    ));
                  }}
                />
                <Tooltip
                  title={
                    `Base Price(${roundFloatToCents(floatVal(subscriptionData.basePrice))}) `
                    + `* Base Price Scalar(${roundFloatToCents(floatVal(subscriptionData.basePriceScalar))}) = `
                    + `${(floatVal(subscriptionData.basePrice ?? 0)
                      * roundFloatToCents(floatVal(subscriptionData.basePriceScalar))).toFixed(2)
                    }`
                  }
                  placement='right'
                >
                  <InfoIcon />
                </Tooltip>
              </span>
            </Grid>
          }
        </>
      }

      {/* Discount Field */}
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Discount *"
          type="text"
          size="small"
          className={Number.isNaN(floatVal(discountRateFormValue)) ? 'required-empty' : ''}
          fullWidth
          sx={{
            background: "white",
            ...getFieldAnimationStyles(),
            ...(Number.isNaN(floatVal(discountRateFormValue)) && getRequiredFieldPulseStyles()),
          }}
          value={discountRateFormValue}
          onChange={(e) => {
            setDiscountRateFormValue(`${parsePercentageInput(e.target.value)}`);
          }}
          onBlur={() => {
            setSubscriptionData((old) => ({
              ...old,
              discountRate: (
                discountRateFormValue === ''
                  ? 0
                  : floatVal(discountRateFormValue)
              ),
            }));
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </Grid>

      {/* Minimum/Maximum Properties for Rate */}
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Minimum properties for rate"
          type="number"
          size="small"
          fullWidth
          sx={{ background: "white" }}
          value={subscriptionData.minimumPropertiesForRate ?? ''}
          onChange={(e) => {
            const value = floatVal(e.target.value);
            if (isNaN(value) || value < 0) return;
            setSubscriptionData((old) => ({
              ...old,
              minimumPropertiesForRate: value ?? null,
            }));
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Maximum properties for rate"
          type="number"
          size="small"
          fullWidth
          sx={{ background: "white" }}
          value={subscriptionData.maxPropertiesForRate ?? ''}
          onChange={(e) => {
            const value = floatVal(e.target.value);
            if (isNaN(value) || value < 0) return;
            setSubscriptionData((old) => ({
              ...old,
              maxPropertiesForRate: value ?? null,
            }));
          }}
        />
      </Grid>
    </Grid>
  );
};
